<script lang="ts">
    import { goto } from "$app/navigation"
    import { isRenderable, isSlippable } from "@circleofa/acim-engine"
    import { movingToTab, selectedTabID, setName, setPath, type TabClass } from "@store/pageTracker"
    import { acim } from "@utils/acim"
    import { convertRangePathToURLPath } from "@utils/fallback-book-redirection"
    import { ValidationError } from "@utils/underhood/errors-extended"
    import { get } from "svelte/store"
    import { tick } from "svelte"

    export let tab: TabClass
    export let kind: "name" | "path" = "name"
    export let displayValue: string | null = null
    export let selectOnFocus = true

    let className = ""
    export { className as class }

    let editing = false
    let committing = false
    let draft = ""
    let input: HTMLInputElement
    let display: HTMLSpanElement
    let pointerStart: {
        id: number
        x: number
        y: number
        moved: boolean
    } | null = null
    let ignoreNextClick = false

    $: value = kind === "name" ? tab.name : tab.path
    $: renderedValue = displayValue ?? value
    $: untitled = kind === "name" && !tab.nameWithoutEmoji
    $: label = kind === "name" ? "Edit tab name" : "Edit tab location"
    $: if (!editing) draft = value

    async function beginEdit(event?: Event) {
        event?.preventDefault()
        event?.stopPropagation()
        draft = value
        editing = true
        await tick()
        input?.focus()
        if (selectOnFocus) input?.setSelectionRange(0, input.value.length)
    }

    function cancelEdit(event?: Event) {
        event?.preventDefault()
        event?.stopPropagation()
        draft = value
        editing = false
    }

    function resolveTabPath(path: string) {
        try {
            const found = acim.find(path)
            if (isRenderable(found)) return found.path
            if (isSlippable(found) && found.slipDirection === "down") return found.slipTo.path
        } catch {
            return null
        }
        return null
    }

    async function savePath(path: string) {
        const resolvedPath = resolveTabPath(path)
        if (!resolvedPath) throw new ValidationError("Enter a valid Course location.")

        tab.path = resolvedPath
        tab.save(true)

        if (get(selectedTabID) === tab.id) {
            setPath(resolvedPath, true, false)
            $movingToTab = true
            await goto("/book/" + convertRangePathToURLPath(resolvedPath))
        }
    }

    async function commit(event?: Event) {
        event?.preventDefault()
        event?.stopPropagation()
        if (!editing || committing) return

        const next = draft.trim()
        if (next === value) {
            editing = false
            return
        }

        committing = true
        input?.setCustomValidity("")

        try {
            if (kind === "name") {
                setName(tab, next)
            } else {
                await savePath(next)
            }
            editing = false
        } catch (error) {
            const message =
                error instanceof ValidationError || error instanceof Error
                    ? error.message
                    : "Unable to save this tab."
            input?.setCustomValidity(message)
            input?.reportValidity()
            await tick()
            input?.focus()
        } finally {
            committing = false
        }
    }

    function handleDisplayKeydown(event: KeyboardEvent) {
        if (event.key !== "Enter" && event.key !== " ") return
        beginEdit(event)
    }

    function handleDisplayPointerdown(event: PointerEvent) {
        if (editing || event.button !== 0) return

        pointerStart = {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            moved: false,
        }
    }

    function handleDisplayPointermove(event: PointerEvent) {
        if (!pointerStart || pointerStart.id !== event.pointerId) return

        const movedX = Math.abs(event.clientX - pointerStart.x)
        const movedY = Math.abs(event.clientY - pointerStart.y)
        if (movedX > 8 || movedY > 8) pointerStart.moved = true
    }

    function handleDisplayPointerup(event: PointerEvent) {
        if (!pointerStart || pointerStart.id !== event.pointerId) return

        const wasTap = !pointerStart.moved
        pointerStart = null
        ignoreNextClick = true
        window.setTimeout(() => {
            ignoreNextClick = false
        })

        if (wasTap) beginEdit(event)
    }

    function handleDisplayPointercancel(event: PointerEvent) {
        if (pointerStart?.id === event.pointerId) pointerStart = null
    }

    function handleDisplayClick(event: MouseEvent) {
        if (ignoreNextClick) {
            event.preventDefault()
            event.stopPropagation()
            return
        }

        beginEdit(event)
    }

    function handleInputKeydown(event: KeyboardEvent) {
        event.stopPropagation()
        if (event.key === "Escape") {
            cancelEdit(event)
            display?.focus()
            return
        }
        if (event.key === "Enter") {
            commit(event)
        }
    }
</script>

<span class={`editable-tab-field ${className}`} class:editing>
    <span
        bind:this={display}
        role="textbox"
        tabindex={editing ? -1 : 0}
        aria-label={label}
        title={`${label}: ${value}`}
        class="display-value"
        class:untitled
        aria-hidden={editing ? "true" : undefined}
        on:click={handleDisplayClick}
        on:keydown={handleDisplayKeydown}
        on:pointercancel={handleDisplayPointercancel}
        on:pointerdown={handleDisplayPointerdown}
        on:pointermove={handleDisplayPointermove}
        on:pointerup={handleDisplayPointerup}
    >
        {editing ? draft || renderedValue || " " : renderedValue}
    </span>

    {#if editing}
        <form class="editor-form" on:submit|preventDefault={commit}>
            <input
                bind:this={input}
                bind:value={draft}
                aria-label={label}
                type="text"
                on:blur={commit}
                on:click|stopPropagation
                on:pointerdown|stopPropagation
                on:keydown={handleInputKeydown}
            />
        </form>
    {/if}
</span>

<style>
    .editable-tab-field {
        position: relative;
        display: inline-grid;
        max-width: 100%;
        min-width: 0;
        line-height: inherit;
        vertical-align: top;
    }

    .display-value {
        display: block;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        border-radius: 0.15rem;
        cursor: text;
        line-height: inherit;
        outline-offset: 2px;
        text-overflow: ellipsis;
        touch-action: pan-x;
        -webkit-user-select: none;
        user-select: none;
        white-space: nowrap;
    }

    .editing .display-value {
        visibility: hidden;
    }

    .display-value:hover {
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 0.18em;
    }

    .display-value.untitled {
        font-style: italic;
    }

    .display-value:focus-visible {
        outline: 2px solid var(--color-gold-400);
    }

    .editor-form {
        position: absolute;
        inset: 0;
        z-index: 2;
        min-width: 0;
        line-height: inherit;
        margin: 0;
    }

    input {
        display: block;
        width: 100%;
        min-width: 1ch;
        height: 100%;
        min-height: 1em;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        letter-spacing: 0;
        line-height: inherit;
        padding: 0;
        touch-action: manipulation;
        -webkit-user-select: text;
        user-select: text;
    }

    input:focus {
        box-shadow: 0 1px 0 currentColor;
        outline: none;
    }

    :global(.darkmode) input {
        background: transparent;
        color: var(--color-grey-50);
    }

    :global(.darkmode) input:focus {
        box-shadow: 0 1px 0 currentColor;
    }
</style>
